import { computed } from 'mobx'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { ConsultationJobType } from '@/features/jobs/job-type'
import type ConsultationManagerViewModel from '@/features/jobs/details/consultation/ConsultationManagerViewModel'
import { consultationExpectationsSteps } from '@/features/jobs/consultations/consultation-display-stage'
import type { DefaultJobConversationViewModel } from '@/features/jobs/details/JobConversationViewModel'
import type { ConsultationInfoGatheringViewModel } from '@/features/jobs/details/consultation-type/info-gathering/ConsultationInfoGatheringViewModel'
import { NEVER, type Observable } from 'rxjs'

/**
 * Tax job specific view model.
 */
export class ConsultationJobDetailsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Observable that emits when the user should be bounced from the consultation job details.
   * Currently consultation jobs don't have any bounce conditions.
   */
  readonly bounce$: Observable<unknown> = NEVER

  constructor(
    public readonly job: SubmittedJob,
    public readonly consultationManagerVm: ConsultationManagerViewModel,
    public readonly jobConversationVm: DefaultJobConversationViewModel,
    public readonly infoGatheringVm: ConsultationInfoGatheringViewModel,
  ) {
    this._lifecycle.manage(this.consultationManagerVm)
    this._lifecycle.manage(this.infoGatheringVm)
  }

  /**
   * The job type.
   */
  @computed
  get type() {
    // We know this is safe.
    return this.job.type as ConsultationJobType
  }

  /**
   * The consultation for the job.
   */
  @computed
  get consultation() {
    return this.consultationManagerVm.consultation
  }

  /**
   * The steps of the consultation expectations dialog.
   */
  @computed
  get expectationSteps() {
    const currentStageIndex = consultationExpectationsSteps.findIndex(
      (step) => step.stage === this.consultation?.displayStage?.type,
    )

    return consultationExpectationsSteps.map((step, index) => ({
      ...step,
      isCompleted: index < currentStageIndex,
      isCurrent: index === currentStageIndex,
    }))
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches data needed for the view to be able to render.
   */
  async fetch(_signal?: AbortSignal) {
    // Nothing to fetch for consultation jobs
  }
}
